// Constant Repository for Dexie Operations
// Handles constants data persistence in IndexedDB for offline-first functionality

import { db } from '@/db/dexieClient';
import { uow, UnitOfWork } from '@/db/unitOfWork';
import type { ConstantStructure, ConstantCacheEntry } from '@/domains/constants/types';

// --- Public API: Get Operations ---
export const getConstantsFromCache = async (): Promise<Record<
  string,
  ConstantStructure
> | null> => {
  try {
    const entries = await db.constants.toArray();
    if (!entries || entries.length === 0) {
      return null;
    }
    // Convert cache entries back to the API response format
    const result: Record<string, ConstantStructure> = {};
    for (const entry of entries) {
      result[entry.typeName] = entry.data;
    }

    return result;
  } catch (error) {
    console.error('Failed to get constants from cache:', error);
    return null;
  }
};

// --- Public API: Update Operations ---
export const saveConstantsToCache = async (
  constants: Record<string, ConstantStructure>
): Promise<void> => {
  try {
    await uow.runRW([db.constants], async () => {
      await db.constants.clear();

      const plainConstants = UnitOfWork.toPlain(constants);

      const entries: ConstantCacheEntry[] = Object.entries(plainConstants).map(
        ([typeName, data]) => ({
          typeName,
          data,
          cachedAt: Date.now(),
        })
      );

      if (entries.length > 0) {
        await db.constants.bulkPut(entries);
      }
    });
  } catch (error) {
    console.error('Failed to save constants to cache:', error);
    throw error;
  }
};
